<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow">
      <!-- Preview Section -->
      <div class="p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Email Preview</h3>
        
        <!-- Subject -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input
            v-model="draftData.subject"
            type="text"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <!-- Sample Greeting -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Sample Greeting</label>
          <div class="text-sm text-gray-600 bg-gray-50 p-3 rounded">
            {{ Object.values(draftData.greetings)[0] || "No greetings generated" }}
          </div>
        </div>

        <!-- Body Paragraphs -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Body Paragraphs</label>
          <div class="space-y-2">
            <div v-for="(paragraph, index) in draftData.body_paragraphs" :key="index" class="flex gap-2">
              <textarea
                v-model="draftData.body_paragraphs[index]"
                rows="3"
                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              ></textarea>
              <button
                @click="removeParagraph(index)"
                class="text-red-600 hover:text-red-700"
                title="Remove paragraph"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
            <button
              @click="addParagraph"
              class="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Paragraph
            </button>
          </div>
        </div>

        <!-- Include Article URL -->
        <div class="mb-4">
          <label class="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="draftData.include_article_url"
              class="sr-only peer"
            >
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            <span class="ml-3 text-sm font-medium text-gray-700">Include Article URL</span>
          </label>
        </div>

        <!-- Closing Phrase -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Closing Phrase</label>
          <input
            v-model="draftData.closing_phrase"
            type="text"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <!-- Recipients Count -->
        <div class="mt-6 bg-gray-50 p-4 rounded-md">
          <p class="text-sm text-gray-600">
            This email will be sent to {{ draftData.recipient_count }} recipient(s)
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between items-center">
        <button
          @click="$emit('cancel')"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <div class="flex gap-4">
          <button
            @click="$emit('regenerate')"
            class="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 border border-transparent rounded-md shadow-sm hover:bg-indigo-200"
          >
            Regenerate
          </button>
          <button
            @click="$emit('approve', draftData)"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
          >
            Approve & Generate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = defineProps<{
  initialDraftData: {
    subject: string
    body_paragraphs: string[]
    include_article_url: boolean
    closing_phrase: string
    greetings: Record<string, string>
    recipient_count: number
    language: string
  }
}>()

const draftData = ref({ ...props.initialDraftData })

const addParagraph = () => {
  draftData.value.body_paragraphs.push('')
}

const removeParagraph = (index: number) => {
  draftData.value.body_paragraphs.splice(index, 1)
}

defineEmits<{
  (e: 'approve', data: typeof draftData.value): void
  (e: 'cancel'): void
  (e: 'regenerate'): void
}>()
</script> 